/*
 * Copyright (c) Arbeidskracht Groep B.V. - all rights reserved
 */

import React from 'react';
import Props, {DispatchProps, StateProps} from './Props';
import {Dispatch} from 'redux';
import {connect} from 'react-redux';
import {
	accountRecoverySceneActivated,
	accountRecoverySceneBackButtonClicked,
	accountRecoverySceneDeactivated,
	accountRecoveryScenePasswordInputChanged,
	accountRecoveryScenePasswordSubmitted,
	accountRecoveryScenePhoneVerificationCodeInputChanged,
	accountRecoveryScenePhoneVerificationCodeSubmitted,
	accountRecoverySceneRequestRestartPhoneVerification,
	accountRecoverySceneRequestRetryPhoneVerification
} from './actions';
import {DisplayType} from './displayType';
import CenteredFormPanel from '../../components/CenteredFormPanel';
import RetryPhoneVerificationStart from '../../components/RetryPhoneVerificationStart';
import AlertWithLoginLink from '../../components/AlertWithLoginLink';
import PhoneVerificationCode from '../../components/PhoneVerificationCode';
import EnterPassword from '../../components/EnterPassword';
import {RootState} from '../../reducers';

class AccountRecovery extends React.Component<Props> {

	componentDidMount() {
		const props: Readonly<Props> = this.props;

		props.onActivate();
	}

	componentWillUnmount() {
		const props: Readonly<Props> = this.props;

		props.onDeactivate();
	}

	render() {
		const props: Readonly<Props> = this.props;

		let display: JSX.Element | null;

		const retryPhoneVerificationStartState = props.retryPhoneVerificationStart;
		const retryPhoneVerificationStartDispatch = props.retryPhoneVerificationStartDispatch;
		const phoneVerificationCodeState = props.phoneVerificationCode;
		const phoneVerificationCodeDispatch = props.phoneVerificationCodeDispatch;
		const enterPasswordState = props.enterPassword;
		const enterPasswordDispatch = props.enterPasswordDispatch;
		switch (props.index.displayType) {
			case DisplayType.DISPLAY_TYPE_RETRY_PHONE_VERIFICATION_START:
				display = <RetryPhoneVerificationStart
					buttonText={retryPhoneVerificationStartState.buttonText}
					submitDisabled={retryPhoneVerificationStartState.submitDisabled}
					onRetryPhoneVerificationStart={retryPhoneVerificationStartDispatch.onRetryPhoneVerificationStart}/>;
				break;
			case DisplayType.DISPLAY_TYPE_PHONE_VERIFICATION_CODE:
				display = <PhoneVerificationCode
					maximumDurationInSeconds={phoneVerificationCodeState.maximumDurationInSeconds}
					verificationStopped={phoneVerificationCodeState.verificationStopped}
					violations={phoneVerificationCodeState.violations.verificationCode}
					submitDisabled={phoneVerificationCodeState.submitDisabled}
					onVerificationCodeChange={phoneVerificationCodeDispatch.onVerificationCodeChange}
					onSubmitVerificationCode={phoneVerificationCodeDispatch.onSubmitVerificationCode}
					onSubmitVerificationRestart={phoneVerificationCodeDispatch.onSubmitVerificationRestart}
					onRetryPhoneVerificationStart={phoneVerificationCodeDispatch.onRetryPhoneVerificationStart}/>;
				break;
			case DisplayType.DISPLAY_TYPE_ENTER_PASSWORD:
				display = <EnterPassword
					strength={enterPasswordState.strength}
					minimumStrength={enterPasswordState.minimumStrength}
					submitDisabled={enterPasswordState.submitDisabled}
					email={enterPasswordState.email}
					violations={enterPasswordState.violations.password}
					onPasswordChange={enterPasswordDispatch.onPasswordChange}
					onSubmitPassword={enterPasswordDispatch.onSubmitPassword}/>;
				break;
			case DisplayType.DISPLAY_TYPE_COMPLETION_MESSAGE:
				display = <AlertWithLoginLink
					reasonMessage="Het wachtwoord is opnieuw ingesteld."
					onLoginClick={props.alertWithLoginLinkDispatch.onLoginClick}/>;
				break;
			case DisplayType.DISPLAY_TYPE_NONE:
			default:
				display = null;
		}

		return (
			<CenteredFormPanel title="Wachtwoord reset" message={props.index.message} messageType={props.index.messageType}>
				{display}
			</CenteredFormPanel>
		);
	}
}

const mapStateToProps = (state: RootState): StateProps => ({
	enterPassword: state.accountRecovery.enterPassword,
	index: state.accountRecovery.index,
	phoneVerificationCode: state.accountRecovery.phoneVerificationCode,
	retryPhoneVerificationStart: state.accountRecovery.retryPhoneVerificationStart
});

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
	alertWithLoginLinkDispatch: {
		onLoginClick: () => dispatch(accountRecoverySceneBackButtonClicked())
	},
	enterPasswordDispatch: {
		onPasswordChange: (password) => dispatch(accountRecoveryScenePasswordInputChanged({evaluationRequest: {password}})),
		onSubmitPassword: (password) => dispatch(accountRecoveryScenePasswordSubmitted({command: {password}}))
	},
	onActivate: () => dispatch(accountRecoverySceneActivated()),
	onDeactivate: () => dispatch(accountRecoverySceneDeactivated()),
	phoneVerificationCodeDispatch: {
		onRetryPhoneVerificationStart: () => dispatch(accountRecoverySceneRequestRetryPhoneVerification()),
		onSubmitVerificationCode: (verificationCode) =>
			dispatch(accountRecoveryScenePhoneVerificationCodeSubmitted({command: {verificationCode}})),
		onSubmitVerificationRestart: () => dispatch(accountRecoverySceneRequestRestartPhoneVerification()),
		onVerificationCodeChange: () => dispatch(accountRecoveryScenePhoneVerificationCodeInputChanged())
	},
	retryPhoneVerificationStartDispatch: {
		onRetryPhoneVerificationStart: () => dispatch(accountRecoverySceneRequestRetryPhoneVerification())
	}
});

export default connect(mapStateToProps, mapDispatchToProps)(AccountRecovery);
